import { ScrollText, ChevronLeft, ChevronRight, X } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { LoadingSpinner } from "@/components/ui/loading";
import { Select } from "@/components/ui/select";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import { formatDateTime } from "@/lib/utils";
import type { AuditLogsPageState } from "../hooks/use-audit-logs-page";

type BadgeVariant =
  | "default"
  | "success"
  | "warning"
  | "error"
  | "info"
  | "secondary";

const RESOURCE_TYPE_OPTIONS = [
  { value: "", label: "All resources" },
  { value: "user", label: "User" },
  { value: "property", label: "Property" },
  { value: "unit", label: "Unit" },
  { value: "device", label: "Device" },
  { value: "alert", label: "Alert" },
  { value: "auth", label: "Auth" },
];

function getActionBadgeVariant(action: string): BadgeVariant {
  if (
    action.includes("deleted") ||
    action.includes("decommissioned") ||
    action.includes("failed")
  )
    return "error";
  if (
    action.includes("created") ||
    action.includes("provisioned") ||
    action.includes("login")
  )
    return "success";
  if (action.includes("updated") || action.includes("acknowledged"))
    return "info";
  if (action.includes("deactivated") || action.includes("resolved"))
    return "warning";
  return "default";
}

export function AuditLogsView({ state }: { state: AuditLogsPageState }) {
  const {
    logs,
    loading,
    totalCount,
    totalPages,
    filters,
    users,
    handleFilterChange,
    handlePageChange,
  } = state;

  const currentPage = filters.page ?? 1;

  const userOptions = [
    { value: "", label: "All users" },
    ...users.map((u) => {
      const fullName = `${u.firstName} ${u.lastName}`.trim();
      return {
        value: u.id,
        label: fullName || u.email,
        description: fullName ? u.email : undefined,
      };
    }),
  ];

  return (
    <div className="p-6 lg:p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Filters */}
        <Card variant="elevated">
          <div className="p-4">
            <div className="flex flex-col sm:flex-row flex-wrap gap-4 items-stretch sm:items-end">
              <div className="sm:w-48">
                <Select
                  label="Resource type"
                  value={filters.resourceType ?? ""}
                  onChange={(value) =>
                    handleFilterChange({ resourceType: value || undefined })
                  }
                  options={RESOURCE_TYPE_OPTIONS}
                />
              </div>
              <div className="sm:w-56">
                <Select
                  label="User"
                  value={filters.userId ?? ""}
                  onChange={(value) =>
                    handleFilterChange({ userId: value || undefined })
                  }
                  options={userOptions}
                  searchable
                />
              </div>
              <div className="sm:w-48">
                <DatePicker
                  label="From date"
                  value={filters.from ?? ""}
                  onChange={(value) => handleFilterChange({ from: value || "" })}
                />
              </div>
              <div className="sm:w-48">
                <DatePicker
                  label="To date"
                  value={filters.to ?? ""}
                  onChange={(value) => handleFilterChange({ to: value || "" })}
                />
              </div>
              {(filters.resourceType || filters.userId || filters.from || filters.to) && (
                <Button
                  variant="ghost"
                  className="text-error-600 hover:text-error-700 w-full sm:w-auto justify-center"
                  onClick={() =>
                    handleFilterChange({ resourceType: "", userId: "", from: "", to: "" })
                  }
                >
                  <X className="w-4 h-4" />
                  Clear filters
                </Button>
              )}
            </div>
          </div>
        </Card>

        {/* Results */}
        {loading ? (
          <Card variant="elevated">
            <div className="flex items-center justify-center py-16">
              <LoadingSpinner />
            </div>
          </Card>
        ) : logs.length === 0 ? (
          <Card variant="elevated">
            <EmptyState
              icon={<ScrollText className="w-16 h-16" />}
              title="No audit logs yet"
              description="Actions taken by users in your organization will appear here."
            />
          </Card>
        ) : (
          <Card variant="elevated" padding="none">
            <Table>
              <TableHeader>
                <TableRow className="whitespace-nowrap">
                  <TableHead>Timestamp</TableHead>
                  <TableHead>Actor</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Resource</TableHead>
                  <TableHead>IP Address</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell className="text-gray-600 dark:text-gray-400 whitespace-nowrap text-sm">
                      {formatDateTime(log.createdAt)}
                    </TableCell>
                    <TableCell>
                      {log.userName ? (
                        <div>
                          <p className="font-medium text-sm">{log.userName}</p>
                          {log.userEmail && (
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {log.userEmail}
                            </p>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400 text-sm">System</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={getActionBadgeVariant(log.action)}
                        size="sm"
                      >
                        {log.action}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">
                      <span className="text-gray-500 dark:text-gray-400 capitalize">
                        {log.resourceType}
                      </span>
                      {log.resourceLabel ? (
                        // Backend resolves users/properties/devices to a
                        // friendly label; render it inline so operators
                        // don't have to translate UUID prefixes.
                        <span className="text-gray-700 dark:text-gray-200 text-xs ml-1">
                          {log.resourceLabel}
                        </span>
                      ) : (
                        <span className="text-gray-400 text-xs ml-1 font-mono">
                          {log.resourceId.length > 8
                            ? `${log.resourceId.slice(0, 8)}…`
                            : log.resourceId}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-gray-500 dark:text-gray-400 text-sm font-mono">
                      {log.ipAddress ?? "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center sm:justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700">
                <p className="hidden sm:block text-sm text-gray-600 dark:text-gray-400">
                  {totalCount} total entries
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage <= 1}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm text-gray-600 dark:text-gray-300">
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage >= totalPages}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </Card>
        )}
      </div>
    </div>
  );
}
